/**
 * Storezone — Tabs
 * WAI-ARIA compliant tablist with keyboard navigation.
 */

import {
  createContext,
  useContext,
  useId,
  useState,
  type ReactNode,
  type KeyboardEvent,
} from 'react'
import './Tabs.css'

interface TabsCtx {
  activeTab: string
  setActiveTab: (id: string) => void
  baseId: string
}

const TabsContext = createContext<TabsCtx | null>(null)

// ---- Root ----
export function Tabs({
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  children,
  className = '',
}: {
  defaultTab?: string
  activeTab?: string
  onChange?: (id: any) => void
  children: ReactNode
  className?: string
}) {
  const baseId = useId()
  const [internalTab, setInternalTab] = useState(defaultTab || '')
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalTab

  const handleTabChange = (id: string) => {
    if (controlledActiveTab === undefined) {
      setInternalTab(id)
    }
    if (onChange) {
      onChange(id)
    }
  }

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab: handleTabChange, baseId }}>
      <div className={`sz-tabs ${className}`}>{children}</div>
    </TabsContext.Provider>
  )
}

// ---- Tab List ----
export function TabList({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div role="tablist" className={`sz-tablist ${className}`}>
      {children}
    </div>
  )
}

// ---- Tab ----
export function Tab({
  id,
  children,
  disabled = false,
}: {
  id: string
  activeTab?: string
  children: ReactNode
  disabled?: boolean
}) {
  const ctx = useContext(TabsContext)
  const activeId = ctx ? ctx.activeTab : ''
  const isActive = activeId === id

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const tabs = document.querySelectorAll<HTMLElement>(`[role="tab"]`)
    const arr = Array.from(tabs)
    const idx = arr.indexOf(e.currentTarget)
    if (e.key === 'ArrowRight') arr[(idx + 1) % arr.length]?.focus()
    if (e.key === 'ArrowLeft') arr[(idx - 1 + arr.length) % arr.length]?.focus()
    if (e.key === 'Home') arr[0]?.focus()
    if (e.key === 'End') arr[arr.length - 1]?.focus()
  }

  return (
    <button
      role="tab"
      id={ctx ? `${ctx.baseId}-tab-${id}` : undefined}
      aria-selected={isActive}
      aria-controls={ctx ? `${ctx.baseId}-panel-${id}` : undefined}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={() => ctx?.setActiveTab(id)}
      onKeyDown={handleKeyDown}
      className={`sz-tab ${isActive ? 'sz-tab--active' : ''}`}
    >
      {children}
    </button>
  )
}

// ---- Tab Panel ----
export function TabPanel({ id, activeTab: propActiveTab, children }: { id: string; activeTab?: string; children: ReactNode }) {
  const ctx = useContext(TabsContext)
  const currentTab = ctx ? ctx.activeTab : propActiveTab
  if (currentTab !== id) return null
  return (
    <div
      role="tabpanel"
      id={ctx ? `${ctx.baseId}-panel-${id}` : undefined}
      aria-labelledby={ctx ? `${ctx.baseId}-tab-${id}` : undefined}
      tabIndex={0}
      className="sz-tabpanel"
    >
      {children}
    </div>
  )
}
